import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

const ROLE_OPTIONS = ["Admin", "Supervisor", "Guard", "Manager", "Security"];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "disapproved", label: "Disapproved" },
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  designation: "",
  role: "Guard",
  status: "pending",
  username: "",
  password: "",
  guard_seq: "",
  memberId: "",
  year_starts: "",
  year_ends: "",
  dept: "",
};

export default function SentinelAddUser() {
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);
  const isEdit = params.get("mode") === "edit";
  const memberIdFromQuery = params.get("member_id") || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [form, setForm] = useState(initialForm);

  const [guards, setGuards] = useState([]);
  const [staffDbConnected, setStaffDbConnected] = useState(true);

  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState({});
  const [selectedSubmodules, setSelectedSubmodules] = useState({});

  const [faceImage, setFaceImage] = useState(null);
  const [facePreview, setFacePreview] = useState("");

  const [licensedUsers, setLicensedUsers] = useState(0);
  const [currentUsers, setCurrentUsers] = useState(0);
  const [limitReached, setLimitReached] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const remainingUsers = useMemo(() => {
    if (licensedUsers > 0) return Math.max(0, licensedUsers - currentUsers);
    return null;
  }, [licensedUsers, currentUsers]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);

      const [dashboardRes, formRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),

        fetch(
          `${API_BASE_URL}/sentinel_users/form-init${
            isEdit ? `?mode=edit&member_id=${encodeURIComponent(memberIdFromQuery)}` : ""
          }`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!formRes.ok) throw new Error("Failed to fetch sentinel form data");

      const dashboardResult = await dashboardRes.json();
      const formResult = await formRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};
      const payload = formResult?.data || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      setLicensedUsers(Number(payload.licensedUsers || 0));
      setCurrentUsers(Number(payload.currentUsers || 0));
      setLimitReached(!!payload.limitReached);

      setGuards(payload.guards || []);
      setStaffDbConnected(payload.staffDbConnected !== false);

      const modulesWithSubs = (payload.modules || []).map((m) => ({
        ...m,
        page_id: String(m.page_id),
        modules: m.modules || m.module_name || "",
        links: m.links || m.module_link || "",
        submodules: payload.subpagesByPage?.[String(m.page_id)] || m.submodules || [],
      }));

      setModules(modulesWithSubs);

      const vm = payload.vm || {};

      setForm({
        ...initialForm,
        fullName: vm.fullName || "",
        email: vm.email || "",
        phone: vm.phone || "",
        designation: vm.designation || "",
        role: vm.role || "Guard",
        status: normalizeStatusValue(vm.status || "pending"),
        username: vm.username || "",
        password: "",
        guard_seq:
          vm.guard_seq === null || vm.guard_seq === undefined ? "" : String(vm.guard_seq),
        memberId: vm.memberId || "",
        year_starts:
          vm.year_starts === null || vm.year_starts === undefined
            ? ""
            : String(vm.year_starts),
        year_ends:
          vm.year_ends === null || vm.year_ends === undefined ? "" : String(vm.year_ends),
        dept: vm.dept === null || vm.dept === undefined ? "" : String(vm.dept),
      });

      if (vm.face_image_path) {
        setFacePreview(vm.face_image_path);
      }

      const existingPermissions = payload.existingPermissions || {};
      const modulesState = {};
      const submodulesState = {};

      modulesWithSubs.forEach((module) => {
        const pageId = String(module.page_id);
        const permission = existingPermissions?.[pageId];
        const enabled = !!permission?.main_access;

        modulesState[pageId] = enabled;
        submodulesState[pageId] = enabled ? Object.keys(permission?.submodules || {}) : [];
      });

      setSelectedModules(modulesState);
      setSelectedSubmodules(submodulesState);
    } catch (error) {
      console.error("Sentinel add user fetch error:", error);
      setErrorMessage(error.message || "Failed to load page data.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "guard_seq") {
      const selectedGuard = guards.find((guard) => {
        const id = getGuardId(guard);
        return String(id) === String(value);
      });

      setForm((prev) => ({
        ...prev,
        guard_seq: value,
        fullName: selectedGuard ? getGuardName(selectedGuard) || prev.fullName : prev.fullName,
        designation: selectedGuard
          ? selectedGuard.designation || selectedGuard.fld_designation || prev.designation
          : prev.designation,
      }));

      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFaceImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please upload a valid image file.");
      return;
    }

    setFaceImage(file);
    setFacePreview(URL.createObjectURL(file));
  };

  const toggleModule = (pageId) => {
    const key = String(pageId);

    setSelectedModules((prev) => {
      const next = !prev[key];

      if (!next) {
        setSelectedSubmodules((old) => ({ ...old, [key]: [] }));
      }

      return { ...prev, [key]: next };
    });
  };

  const toggleSubmodule = (pageId, submoduleName) => {
    const key = String(pageId);

    if (!selectedModules[key]) return;

    setSelectedSubmodules((prev) => {
      const current = prev[key] || [];
      const exists = current.includes(submoduleName);

      return {
        ...prev,
        [key]: exists
          ? current.filter((item) => item !== submoduleName)
          : [...current, submoduleName],
      };
    });
  };

  const buildPayload = () => {
    const perm_module = {};
    const perm_sub = {};

    Object.entries(selectedModules).forEach(([pageId, enabled]) => {
      if (enabled) {
        perm_module[pageId] = true;
        perm_sub[pageId] = selectedSubmodules[pageId] || [];
      }
    });

    return {
      form_mode: isEdit ? "edit" : "create",
      fullName: form.fullName,
      email: form.email,
      phone: form.phone,
      designation: form.designation,
      role: form.role,
      status: form.status,
      username: form.username,
      password: form.password,
      guard_seq: form.guard_seq === "" ? null : Number(form.guard_seq),
      memberId: form.memberId,
      year_starts: form.year_starts === "" ? null : Number(form.year_starts),
      year_ends: form.year_ends === "" ? null : Number(form.year_ends),
      dept: form.dept === "" ? null : Number(form.dept),
      created_by_admin_id: Number(localStorage.getItem("admin_id") || 1),
      perm_module,
      perm_sub,
    };
  };

  const buildFormData = () => {
    const payload = buildPayload();

    if (isEdit && !String(form.password || "").trim()) {
      delete payload.password;
    }

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      if (key === "perm_module" || key === "perm_sub") {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });

    if (faceImage) {
      formData.append("face_image", faceImage);
    }

    return formData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.fullName.trim() || !form.username.trim() || !form.role.trim()) {
      setErrorMessage("Please fill all required fields.");
      return;
    }

    if (!form.memberId.trim()) {
      setErrorMessage("Member ID is required.");
      return;
    }

    if (!isEdit && !form.password.trim()) {
      setErrorMessage("Password is required.");
      return;
    }

    if (!isEdit && !faceImage) {
      setErrorMessage("Guard face image is required for face verification.");
      return;
    }

    if (!isEdit && limitReached) {
      setErrorMessage(
        `User limit reached (${currentUsers} of ${licensedUsers}). You cannot add more users.`
      );
      return;
    }

    try {
      setSaving(true);
      setSuccessMessage("");
      setErrorMessage("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/sentinel_users/${isEdit ? "update" : "create"}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: buildFormData(),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save sentinel user");
      }

      if (isEdit) {
        setSuccessMessage("Sentinel user updated successfully!");
      } else {
        const createdMemberId = data?.data?.memberId || data?.data?.user?.fld_memberId || form.memberId;
        const nextMemberId = data?.data?.nextMemberId || "";

        setSuccessMessage(`Sentinel user created successfully! Member ID: ${createdMemberId}`);
        navigate('/sentinel_user_list')

        setForm({
          ...initialForm,
          status: "pending",
          role: "Guard",
          memberId: nextMemberId,
        });

        setSelectedModules({});
        setSelectedSubmodules({});
        setFaceImage(null);
        setFacePreview("");
        setCurrentUsers((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Save sentinel user error:", error);
      setErrorMessage(error.message || "Failed to save sentinel user.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading sentinel add user page...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel_user_list"
      activeMenu="users"
    >
      <PageHeader isEdit={isEdit} onBack={() => navigate("/sentinel_user_list")} />

      <LicenseBanner
        licensedUsers={licensedUsers}
        currentUsers={currentUsers}
        remainingUsers={remainingUsers}
        limitReached={limitReached}
      />

      {successMessage ? <Alert type="success" message={successMessage} /> : null}
      {errorMessage ? <Alert type="error" message={errorMessage} /> : null}

      <form onSubmit={handleSubmit}>
        <FormSection
          title="Basic Information"
          subtitle={isEdit ? "Modify sentinel user profile details" : "Enter sentinel user profile details"}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Field label="Full Name *">
              <Input name="fullName" value={form.fullName} onChange={handleChange} required />
            </Field>

            <Field label="Email">
              <Input type="email" name="email" value={form.email} onChange={handleChange} />
            </Field>

            <Field label="Phone">
              <Input name="phone" value={form.phone} onChange={handleChange} />
            </Field>

            <Field label="Designation">
              <Input name="designation" value={form.designation} onChange={handleChange} />
            </Field>

            <Field label="Role *">
              <Select name="role" value={form.role} onChange={handleChange} required>
                <option value="">Select role</option>
                {ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Status">
              <Select name="status" value={form.status} onChange={handleChange}>
                {STATUS_OPTIONS.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Year Starts">
              <Input
                type="number"
                name="year_starts"
                value={form.year_starts}
                onChange={handleChange}
              />
            </Field>

            <Field label="Year Ends">
              <Input
                type="number"
                name="year_ends"
                value={form.year_ends}
                onChange={handleChange}
              />
            </Field>

            <Field label="Department">
              <Input type="number" name="dept" value={form.dept} onChange={handleChange} />
            </Field>
          </div>
        </FormSection>

        <FormSection
          title="Face Verification"
          subtitle="Upload a clear front-face photo for sentinel face verification"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label={isEdit ? "Guard Face Image" : "Guard Face Image *"}>
              <Input
                type="file"
                accept="image/*"
                onChange={handleFaceImageChange}
                required={!isEdit}
              />
              <p className="mt-2 text-xs text-slate-500" style={{ fontFamily: FONT_UI }}>
                Use a clear image with only one face. Avoid mask, blur, side face, or dark lighting.
              </p>
            </Field>

            {facePreview ? (
              <div>
                <p
                  className="mb-2 block text-sm font-semibold text-slate-900"
                  style={{ fontFamily: FONT_UI }}
                >
                  Face Preview
                </p>

                <img
                  src={facePreview}
                  alt="Guard face preview"
                  className="h-36 w-36 rounded-xl border border-slate-200 object-cover shadow-sm"
                />

                <button
                  type="button"
                  onClick={() => {
                    setFaceImage(null);
                    setFacePreview("");
                  }}
                  className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-600"
                  style={{ fontFamily: FONT_UI }}
                >
                  Remove Image
                </button>
              </div>
            ) : null}
          </div>
        </FormSection>

        <FormSection title="Login Credentials" subtitle="Set username, password, and staff mapping">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Field label="Username *">
              <Input name="username" value={form.username} onChange={handleChange} required />
            </Field>

            <Field label={isEdit ? "Password (leave blank to keep current)" : "Password *"}>
              <Input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                required={!isEdit}
              />
            </Field>

            <Field label="Guard (link to staff)">
              <Select name="guard_seq" value={form.guard_seq} onChange={handleChange}>
                <option value="">-- Select Guard --</option>
                {!staffDbConnected ? (
                  <option value="" disabled>
                    Staff DB connection failed
                  </option>
                ) : (
                  guards.map((guard) => {
                    const id = getGuardId(guard);
                    const appNo = guard.application_number || guard.staff_code || guard.emp_code || "";
                    const name = getGuardName(guard);
                    const desig = guard.designation || guard.fld_designation || "";
                    const label = `${appNo ? `${appNo} - ` : ""}${name}${
                      desig ? ` (${desig})` : ""
                    }`.trim();

                    return (
                      <option key={id} value={String(id)}>
                        {label || id}
                      </option>
                    );
                  })
                )}
              </Select>
            </Field>

            <Field label="Member ID">
              <Input
                name="memberId"
                value={form.memberId}
                readOnly
                onChange={handleChange}
                className="bg-slate-50 text-slate-500"
              />
            </Field>
          </div>
        </FormSection>

        <FormSection
          title="Module Access & Permissions"
          subtitle="Toggle modules and choose submodule access for sentinel guard"
        >
          <div className="grid gap-5">
            {modules.length === 0 ? (
              <EmptyState>No modules found.</EmptyState>
            ) : (
              modules.map((module) => (
                <ModuleCard
                  key={module.page_id}
                  module={module}
                  enabled={!!selectedModules[String(module.page_id)]}
                  selectedSubs={selectedSubmodules[String(module.page_id)] || []}
                  onToggleModule={toggleModule}
                  onToggleSubmodule={toggleSubmodule}
                />
              ))
            )}
          </div>
        </FormSection>

        <div className="mb-5 mt-2 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/sentinel_user_list")}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
          >
            Back to User List
          </button>

          <button
            type="submit"
            disabled={saving || (!isEdit && limitReached)}
            className="rounded-xl bg-gradient-to-r from-slate-800 to-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70"
            style={{ fontFamily: FONT_UI }}
          >
            {saving ? "Saving..." : isEdit ? "Update User" : "+ Create User"}
          </button>
        </div>
      </form>
    </Layout>
  );
}

function ModuleCard({ module, enabled, selectedSubs, onToggleModule, onToggleSubmodule }) {
  const skin = useSkin();
  const subs = module.submodules || [];

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div
        className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 text-white"
        style={{ background: skin.gradient }}
      >
        <div>
          <div className="text-[17px] font-semibold" style={{ fontFamily: FONT_UI }}>
            {module.modules}
          </div>

          {module.links ? (
            <div className="mt-1 text-xs text-white/80" style={{ fontFamily: FONT_UI }}>
              {module.links}
            </div>
          ) : null}
        </div>

        <label
          className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold"
          style={{ fontFamily: FONT_UI }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={() => onToggleModule(module.page_id)}
            className="h-4 w-4"
          />
          Enable Module
        </label>
      </div>

      <div className="p-5">
        {subs.length === 0 ? (
          <EmptyState>No sub-modules. Enabling grants module access.</EmptyState>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {subs.map((sub) => {
              const subName = sub.submodules || sub.submodule_name || "";
              const checked = selectedSubs.includes(subName);

              return (
                <label
                  key={`${module.page_id}-${sub.subpage_id || subName}`}
                  className={`flex items-start gap-3 rounded-xl border border-slate-200 p-3 ${
                    enabled ? "cursor-pointer bg-white" : "cursor-not-allowed bg-slate-50 opacity-60"
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={!enabled}
                    checked={checked}
                    onChange={() => onToggleSubmodule(module.page_id, subName)}
                    className="mt-1 h-4 w-4"
                  />

                  <div>
                    <div
                      className="text-sm font-semibold text-slate-900"
                      style={{ fontFamily: FONT_UI }}
                    >
                      {subName}
                    </div>

                    {sub.links ? (
                      <div className="mt-1 text-xs text-slate-500" style={{ fontFamily: FONT_UI }}>
                        {sub.links}
                      </div>
                    ) : null}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function PageHeader({ isEdit, onBack }) {
  const skin = useSkin();

  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal tracking-tight text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          Sentinel User Management
        </h2>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {isEdit
            ? "Update existing sentinel user details and permissions"
            : "Create a new sentinel user and assign permissions"}
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-white px-7 py-6 shadow-sm">
        <div>
          <h3
            className="m-0 text-[22px] font-normal text-slate-900"
            style={{ fontFamily: FONT_HEADING }}
          >
            {isEdit ? "Edit Sentinel User" : "Add Sentinel User"}
          </h3>

          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Fill the form below to {isEdit ? "update the account" : "create the account"}
          </p>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
          style={{
            background: skin.gradient,
            fontFamily: FONT_UI,
          }}
        >
          Back to User List
        </button>
      </div>
    </>
  );
}

function LicenseBanner({ licensedUsers, currentUsers, remainingUsers, limitReached }) {
  return (
    <div
      className={`mb-5 rounded-xl border px-4 py-3.5 text-sm font-medium ${
        limitReached
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-green-200 bg-green-50 text-green-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      <strong>User Capacity:</strong>{" "}
      {licensedUsers > 0
        ? `${currentUsers} / ${licensedUsers}`
        : `${currentUsers} (no limit set)`}
      {" · "}
      {limitReached
        ? "Limit reached - new user creation is disabled."
        : licensedUsers > 0
        ? `You can add ${remainingUsers} user(s).`
        : "You can add more user(s)."}
    </div>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 rounded-xl border px-4 py-3.5 text-sm font-medium ${
        isSuccess
          ? "border-green-200 bg-green-50 text-green-700"
          : "border-red-200 bg-red-50 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function FormSection({ title, subtitle, children }) {
  return (
    <div className="mb-6 rounded-2xl border border-slate-100 bg-white px-7 py-6 shadow-sm">
      <div className="mb-5">
        <h3
          className="m-0 text-[22px] font-normal text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          {title}
        </h3>

        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {subtitle}
        </p>
      </div>

      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label
        className="mb-2 block text-sm font-semibold text-slate-900"
        style={{ fontFamily: FONT_UI }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`box-border w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 ${className}`}
      style={{ fontFamily: FONT_UI }}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`box-border w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 ${className}`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </select>
  );
}

function EmptyState({ children }) {
  return (
    <div
      className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </div>
  );
}

function normalizeStatusValue(value) {
  const status = String(value || "").toLowerCase();

  if (status === "approved") return "approved";
  if (status === "disapproved") return "disapproved";
  return "pending";
}

function getGuardId(guard) {
  return (
    guard.staff_seq ||
    guard.seq ||
    guard.id ||
    guard.staff_id ||
    guard.emp_id ||
    guard.employee_id ||
    guard.fld_staff_id ||
    guard.fld_emp_id ||
    ""
  );
}

function getGuardName(guard) {
  return (
    guard.staff_name ||
    guard.name ||
    guard.employee_name ||
    guard.full_name ||
    guard.fld_name ||
    ""
  );
}

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}